import { createApp } from 'vue'
import { createPinia } from 'pinia'
import ElementPlus from 'element-plus'
import 'element-plus/dist/index.css'
// 海图室 V1.1 设计令牌 + Element Plus 主题映射（必须在 EP 样式之后引入以覆盖变量）
import './styles/tokens.css'
import * as ElementPlusIconsVue from '@element-plus/icons-vue'
import zhCn from 'element-plus/es/locale/lang/zh-cn'
import { ElMessage } from 'element-plus'

import App from './App.vue'
import router from './router'
import { vPermission } from './directives/permission'
import { vNumberBlur } from './directives/number-blur'

const app = createApp(App)

// 注册所有图标
for (const [key, component] of Object.entries(ElementPlusIconsVue)) {
  app.component(key, component)
}

app.use(createPinia())
app.use(router)
app.use(ElementPlus, { locale: zhCn })
app.directive('permission', vPermission)
app.directive('number-blur', vNumberBlur)

/**
 * 全局数字输入框失焦校验（事件委托，覆盖所有 el-input-number）——
 * 不需要给每个字段加 v-number-blur 指令。失焦时根据 Element Plus
 * 渲染到 DOM 的 aria-valuemin/max + 字段自身 string 值做校验，
 * 非空 / 数字 / 范围 / 小数位超限 都会 ElMessage 提示并自动修正。
 */
function setupGlobalNumberBlur() {
  if (typeof document === 'undefined') return
  document.addEventListener(
    'blur',
    (e: Event) => {
      const t = e.target as HTMLInputElement
      if (!t || !t.classList?.contains('el-input__inner')) return
      const wrapper = t.closest('.el-input-number') as HTMLElement | null
      if (!wrapper) return
      // 已经用 v-number-blur 单独绑定的不重复处理
      if (wrapper.hasAttribute('data-blur-handled')) return

      const raw = t.value
      if (raw === '' || raw === null) return
      const num = Number(raw)
      if (Number.isNaN(num) || !isFinite(num)) {
        ElMessage.error('请输入有效数字')
        t.value = ''
        t.dispatchEvent(new Event('input', { bubbles: true }))
        return
      }
      // 读取约束（Element Plus 在 input 上设了 aria-valuemin/max）
      const minAttr = wrapper.getAttribute('aria-valuemin') ?? t.getAttribute('aria-valuemin')
      const maxAttr = wrapper.getAttribute('aria-valuemax') ?? t.getAttribute('aria-valuemax')
      // Element Plus 默认 min=-Infinity，max=Infinity，所以 None/undefined 视为不限制
      const min = minAttr !== null && minAttr !== undefined && minAttr !== '' ? Number(minAttr) : undefined
      const max = maxAttr !== null && maxAttr !== undefined && maxAttr !== '' ? Number(maxAttr) : undefined

      // 负数检查（min undefined 时也允许负数？保守起见：min 未设置时不强制非负）
      if (min !== undefined && !isNaN(min) && num < min) {
        ElMessage.error(`数值不能小于 ${min}`)
        t.value = String(min)
        t.dispatchEvent(new Event('input', { bubbles: true }))
        return
      }
      if (max !== undefined && !isNaN(max) && num > max) {
        ElMessage.error(`数值不能大于 ${max}`)
        t.value = String(max)
        t.dispatchEvent(new Event('input', { bubbles: true }))
        return
      }
      // 精度检查：Element Plus 不会把 precision 渲染到 DOM，保守不强制精度校验
      // （需要严格精度的字段可在模板上加 v-number-blur="{ precision: N }" 显式指令）
    },
    true  // capture phase，确保比 v-number-blur 优先 / 与之不冲突
  )
}
setupGlobalNumberBlur()

app.mount('#app')
